/**
 * Copyright (c) Microsoft. All rights reserved.
 */

// tslint:disable:no-any
// tslint:disable:no-unsafe-any
// tslint:disable:strict-boolean-expressions
// tslint:disable:max-classes-per-file
// tslint:disable:promise-function-async

import { createRegistrarClient, IRegistrarClient, IRegistrarClientOptions } from '@skype/tsregistrar';
import { Promise } from 'es6-promise';
import { calculateExpireTsInSec, calculateTtlInSec, Timespan, toJson } from './Common';
import { ClientEventName, ConnectionTracker, Properties, ResponseData } from './ConnectionTracker';
import { CLIENT_VERSION } from './Constants';
import { ExponentialBackoff } from './ExponentialBackoff';
import {
    HttpHeaders, LogProvider, SendResponseResult, SkypeTokenProvider, TimeoutOptions,
    UserActivityState
} from './Interfaces';
import {
    IClientInfo, IMessageLossIndicator, ISocketIoOptions, ITrouterOptions, ITrouterRequest, ITrouterResponse,
    ServerState, UserActivityEventReason
} from './InternalInterfaces';
import { Logger } from './Logger';
import { IWorker, TrouterFsm } from './TrouterFsm';
import { UserActivityObject } from './TrouterManager';

// This is the current API version
const VERSION: string = 'v4';

// Default value for registration timeout
const DEFAULT_REGISTRATION_TIMEOUT_SEC = 3600;

// tslint:disable:no-var-requires
// tslint:disable:no-require-imports
// tslint:disable-next-line:no-reserved-keywords
declare function require(arg: string): any;
const io: any = require('./socket.io');

/**
 * Stub for IClientInfo
 */
class ClientInfo implements IClientInfo {
    public cv: string = CLIENT_VERSION;
    public ua: string = '';
    public hr: string = '';
    public v: string = '';
}

/**
 * Stub for ISocketIoOptions
 */
class SocketIoOptions implements ISocketIoOptions {
    public 'force new connection'?: boolean = true;
    public 'reconnect'?: boolean = false;
    public 'query'?: string = '';
    public ackTimeoutMs = 5000;
}

/**
 * Interface to work with SocketIO library
 */
interface ISocketIo {
    socketNumber: number;
    socket: any;

    emit(event: string, handler: any): void;
    emit(event: string, args: object, handler: any): void;
    on(event: string, handler: any): void;
    disconnect(): void;
    send(msg: string);
    removeAllListeners(event: string);
}

/**
 * Params received during handshake
 */
interface ITrouterConnectedEventArgs {
    // Time it took Trouter to perform the handshake request internally, in milliseconds. Number in quotes, e.g. "123"
    dur: string;
    // Connection valid TTL in seconds as returned by service.
    ttl: number;
}

/**
 * Params received on message lost event
 */
interface ITrouterMessageLossEventArgs {
    droppedIndicators: IMessageLossIndicator[];
}

type TrouterRequestCallbackType = (val: string) => void;

const cvHeader: string = 'MS-CV';

/**
 * Request from downstream service
 */
class TrouterRequest implements ITrouterRequest {
    public startTS: number;
    public url: string;
    public shortUrl: string;
    public body: string;
    public headers: HttpHeaders;
    public id: number;
    public method: string;
    public replied: boolean;
    public timedout: boolean;
    public timeoutTimerId: number;

    private dataCallback: TrouterRequestCallbackType | undefined;
    private cvCounter: number = 0;
    private receivedCv: string;

    public constructor(requestJson: string) {
        const json = JSON.parse(requestJson);

        this.startTS = json.hasOwnProperty('startTS') ? json.startTS : 0;
        this.url = json.hasOwnProperty('url') ? json.url : '';
        this.shortUrl = json.hasOwnProperty('shortUrl') ? json.shortUrl : '';
        this.body = json.hasOwnProperty('body') ? json.body : '';
        this.headers = json.hasOwnProperty('headers') ? json.headers : {};
        this.id = (json.hasOwnProperty('id') && typeof json.id === 'number') ? json.id : -1;
        this.method = json.hasOwnProperty('method') ? json.method : '';

        this.replied = false;
        this.timedout = false;
        this.timeoutTimerId = 0;

        this.receivedCv = this.headers[cvHeader];
        this.updateCvHeader();
    }

    public get correlationVector(): string {
        return this.receivedCv ? `${this.receivedCv}.${this.cvCounter}` : '';
    }

    public on(target: string, targetCallback: (val?: string) => void) {
        if (target === 'data') {
            this.dataCallback = targetCallback;
        } else if (target === 'end') {
            if (typeof this.dataCallback === 'function') {
                this.dataCallback(this.body);
            }
            targetCallback();
        }
    }

    public incrementCorrelationVector() {
        ++this.cvCounter;
        this.updateCvHeader();
    }

    private updateCvHeader(): void {
        const cv: string = this.correlationVector;
        if (cv) {
            this.headers[cvHeader] = cv;
        }
    }
}

/**
 * Response to downstream service
 */
class TrouterResponse implements ITrouterResponse {
    public constructor(
        private request: TrouterRequest,
        private responseData: ResponseData,
        private sendResponse: (request: TrouterRequest, responseData: ResponseData) => SendResponseResult) {
    }

    public writeHead(status: number, headers?: HttpHeaders) {
        this.responseData.status = status;
        this.responseData.headers = headers;
    }

    public write(chunk: string) {
        this.responseData.body += chunk;
    }

    public end(chunk?: string): SendResponseResult {
        if (chunk) {
            this.responseData.body += chunk;
        }

        return this.sendResponse(this.request, this.responseData);
    }
}

/**
 * An client-initiated event that is to be sent to trouter.
 */
class DownstreamEvent {
    public name: string;
    public args: object;
    public timeoutTimerId: number;

    public constructor(name: string) {
        this.name = name;
        this.args = {};
        this.timeoutTimerId = 0;
    }
}

/**
 * Interface to be implemented by Trouter consumer
 */
export interface IManagerConsumer {
    /** Called on received request */
    onDownstreamRequest(connection: TrouterConnection, request: ITrouterRequest, response: ITrouterResponse);
    /** Called once connection is established */
    onConnected(connection: TrouterConnection);
    /** Called once registration in EDF is succeed */
    onRegistered(connection: TrouterConnection);
    /** Called if registration in EDF fails or expires */
    onUnregistered(connection: TrouterConnection);
    /** Called once connection failed */
    onReconnecting(connection: TrouterConnection);
    /** Called when server asked to reconnect */
    onReconnectIsRequired(connection: TrouterConnection);
    /** Called once connection is unregistered and closed */
    onDisconnected(connection: TrouterConnection);
    /** Called when connection parameters are updated */
    onConnectionParametersUpdated(connectParams: {});
    /** Called when message loss indicator received */
    onTrouterMessageLost(flowTags: IMessageLossIndicator[]);
    /** Called once user activity state is accepted */
    onUserActivityStateAccepted?(cv: string);
}

/**
 * Allocation result from Trouter server
 */
export interface IAllocateResult {
    url: string;
    surl: string;
    curlb: string;
    connectparams: any;
    socketio: string;
    id: string;
    ccid: string;
    ttl: string;
}

/**
 * Trouter + registration flow implementation
 */
export class TrouterConnection implements IWorker {
    private logger: Logger;
    private healthBackoff: ExponentialBackoff;
    private tokenBackoff: ExponentialBackoff;
    private fsm: TrouterFsm;
    private connectionTracker: ConnectionTracker;
    private registrarClient?: IRegistrarClient;

    private connectionId = '';

    private inIncallMode = false;
    private timeoutOptions: TimeoutOptions;
    private connectionTimeoutId?: number;
    private pingTimerId?: number;
    private pingResponseTimerId?: number;
    private registrationTimerId?: number;
    private incallModeTimerId?: number;

    private socket?: ISocketIo;
    private allocateResult?: IAllocateResult;
    /** expiration's timestamp of allocated connectionID */
    private connectionExpireTimestampInSecs?: number;
    private externalConnectParams?: any;

    /** Allocated URL where library was able to successfully connect */
    private connectedUrl?: string;

    /** Name of currently used transport */
    private transportTypeName: string | undefined;

    private clientID: number;
    private connectionAttempt = 0;
    private connectedClientId = '';
    private domId: string | undefined;
    private c2cUrlBase: string = '';

    private clientInfo: IClientInfo;
    /** Used to calculate short URL in requests */
    private urlPath: string | undefined;
    private userActivityState: UserActivityObject;
    private pendingTimers: { [id: number]: string; } = { };

    private lastDisconnectReason: string = '';

    private readonly UNKNOWN_TRANSPORT = 'unknown_transport';

    constructor(
        logProvider: LogProvider,
        private options: ITrouterOptions,
        private manager: IManagerConsumer,
        private tokenProvider: SkypeTokenProvider,
        initialUserActivityState: UserActivityObject
    ) {
        this.logger = new Logger('Connection', logProvider);
        this.timeoutOptions = this.options.timeoutOptions;
        this.healthBackoff = new ExponentialBackoff(this.logger, this.timeoutOptions.maxBackoffMs);
        this.tokenBackoff = new ExponentialBackoff(this.logger, this.timeoutOptions.maxBackoffMs);
        this.clientID = Date.now();

        // tslint:disable-next-line:no-typeof-undefined - window exists only in browser
        if ((typeof window !== 'undefined') && window.location) {
            this.domId = window.location.hostname;
        }

        this.clientInfo = new ClientInfo();
        this.clientInfo.cv = CLIENT_VERSION;
        this.clientInfo.ua = '';

        if (this.options && this.options.clientInfo) {
            this.clientInfo.ua = this.safeString(this.options.clientInfo.ua);
            this.clientInfo.v = this.safeString(this.options.clientInfo.v);
        }

        this.connectionTracker = new ConnectionTracker(
            logProvider,
            this.clientID,
            this.clientInfo,
            () => { return this.getServerState(); },
            this.options.endpointId,
            this.options.clientCorrelationID,
            this.options.environment
        );
        this.applyConnectionTrackerOptions(options);

        const useIncallMode = this.options.incallModeTimeoutMs > 0;
        this.fsm = new TrouterFsm(logProvider, this, useIncallMode);

        if (options.registration) {
            const registrarOptions: IRegistrarClientOptions = {
                registrarUrl: options.registration.registrarUrl,
                registrationId: options.registration.registrationId,
                requestTimeoutMs: options.timeoutOptions.fetchTimeoutMs,
                initialRetryDelayMs: 1000,
                maxRetryDelayMs: options.timeoutOptions.maxBackoffMs
            };
            this.registrarClient = createRegistrarClient(logProvider, this.tokenProvider, registrarOptions);
        }

        this.userActivityState = initialUserActivityState;
    }

    public start(externalConnectParams?: any) {
        this.logger.info('Starting');

        this.externalConnectParams = externalConnectParams;

        this.fsm.start();
    }

    public stop(dontUnregister: boolean) {
        this.logger.info('Stopping');
        this.connectionTracker.close();
        this.fsm.stop(dontUnregister);
    }

    public configure(options: ITrouterOptions) {
        // Currently only baseUrl and telemetrySettings options are supported for reconfiguration
        const urlChanged = this.options.trouterUrl !== options.trouterUrl;
        this.options = options;

        this.applyConnectionTrackerOptions(options);

        if (urlChanged) {
            this.onTrouterReconnect();
        }
    }

    public checkConnection(disconnectDetected: boolean) {
        this.logger.info(`checkConnection called with ${disconnectDetected}`);

        this.fsm.checkConnection(disconnectDetected);

        // Don't send on disconnectDetected === false, because Teams client calls it on timer
        if (disconnectDetected) {
            this.connectionTracker.sendTelemetry(ClientEventName.CheckConnection, {
                disconnectDetected: disconnectDetected
            }, []);
        }
    }

    /** Returns info about connection */
    public getServerState(): ServerState {
        return new ServerState(
            this.connectionId,
            this.connectedClientId,
            this.domId ? this.domId : '',
            this.allocateResult ? this.allocateResult.url : '',
            this.allocateResult ? this.allocateResult.surl : '',
            this.c2cUrlBase,
            this.connectionExpireTimestampInSecs);
    }

    //////// Implement IWorker ////////

    public getToken(useCache: boolean, backoff: boolean) {
        this.logger.info(`Getting token ${backoff ? 'with backoff' : 'without backoff'}`);

        const requestFunction = () => {
            this.connectionTracker.trackStart('token');

            // tslint:disable-next-line:no-floating-promises
            this.tokenProvider(!useCache).then((token) => {
                this.logger.debug('token is received');
                this.connectionTracker.trackEnd('token');
                this.fsm.onTokenReceived(token);
            }).catch((error: Error) => {
                const msg = toJson(error.stack);
                this.logger.error(`Getting token failed, will retry after timeout. Error: ${msg}`);
                this.connectionTracker.trackError('token', msg);
                this.getToken(useCache, true);
            });
        };

        if (backoff) {
            this.tokenBackoff.backoff('getting token', requestFunction);
        } else {
            this.tokenBackoff.reset();
            requestFunction();
        }
    }

    public startConnectionTimer() {
        this.stopConnectionTimer();
        this.logger.debug(`Starting connection timeout for ${this.timeoutOptions.connectionTimeoutMs} ms`);
        this.connectionTimeoutId = setTimeout(() => {
            this.logger.info('Connection timeout is fired');
            this.fsm.onConnectingTimeout();
        }, this.timeoutOptions.connectionTimeoutMs);
    }

    public stopConnectionTimer() {
        if (this.connectionTimeoutId) {
            this.logger.debug('Stopping connection timeout');
            clearTimeout(this.connectionTimeoutId);
            this.connectionTimeoutId = undefined;
        }
    }

    public startPingTimer() {
        if (this.transportTypeName === 'websocket') {
            this.logger.debug(`Starting ping timeout for ${this.timeoutOptions.pingTimeoutMs} ms`);
            this.pingTimerId = setInterval(() => {
                this.logger.info('Ping interval fired');
                this.fsm.onPingInterval();
            }, this.timeoutOptions.pingTimeoutMs);
        } else {
            this.logger.debug(`Not starting ping for transport ${this.transportTypeName}`);
        }
    }

    public stopPingTimer() {
        if (this.pingTimerId) {
            this.logger.debug('Stopping ping timeout');
            this.clearPingResponseTimer();
            clearInterval(this.pingTimerId);
            this.pingTimerId = undefined;
        }
    }

    public shouldSkipRegistration() {
        return this.options.registration === undefined;
    }

    public hasCustomRegisterTtl() {
        return this.options.registration && this.options.registration.registrarTtlSec;
    }

    public startRegisterTimer() {
        if (this.registrationTimerId !== undefined) {
            this.stopRegisterTimer();
        }

        const timeForReregistrationSec = 30;
        const ttl = this.getRegistrationTtl() - timeForReregistrationSec;
        this.logger.debug(`Starting registration timeout for ${ttl} sec`);
        this.registrationTimerId = setTimeout(() => {
            this.logger.info('Registration timeout is fired');
            this.registrationTimerId = setTimeout(() => {
                this.registrationTimerId = undefined;
                this.logger.debug('Re-registration did not happen in time');
                this.dispatchUnregistered();
            }, timeForReregistrationSec * 1000);
            this.fsm.onRegistrationTimeout();
        }, ttl * 1000);
    }

    public stopRegisterTimer() {
        if (this.registrationTimerId) {
            this.logger.debug('Stopping registration timeout');
            clearTimeout(this.registrationTimerId);
            this.registrationTimerId = undefined;
        }
    }

    public resendRegistration(): Promise<void> {
        if (!this.registrarClient) {
            throw new Error('Trouter Client not configured to handle registrations');
        }

        return this.registrarClient.resendRegistration();
    }

    public buildSocketIoUrlParams(): string {
        if (!this.allocateResult) {
            throw new Error('Allocate result is undefined in buildSocketIoUrlParams()');
        }

        const urlParams: any = {};

        // Copy connection parameters (i.e. signature) from allocation response
        if (this.allocateResult.connectparams) {
            const signatureData = this.allocateResult.connectparams;
            for (const i of Object.keys(signatureData)) {
                if (signatureData.hasOwnProperty(i) && signatureData[i] !== undefined) {
                    urlParams[i] = signatureData[i];
                }
            }
        }

        // And add some more fields
        urlParams.v = VERSION;
        urlParams.tc = encodeURI(toJson(this.clientInfo));
        urlParams.timeout = this.timeoutOptions.pingTimeoutMs / 1000;
        urlParams.auth = 'true';

        if (this.options.endpointId) {
            urlParams.epid = this.options.endpointId;
        }

        if (this.userActivityState.state !== UserActivityState.Unknown) {
            urlParams.userActivity = encodeURI(toJson(this.userActivityState.toEventObject()));
        }

        return this.appendConnectedClientIds(this.buildQuery(urlParams), true);
    }

    public startSocketIo() {
        this.logger.info('Starting socket io');
        this.connectionTracker.trackStart('connectSocket');
        //this.connectionAttempt++;
        if (!this.allocateResult) {
            throw new Error('Allocate result is undefined in startSocketIo()');
        }

        const options: ISocketIoOptions = this.options.ioOptions || new SocketIoOptions();

        // Regardless of any passed in socket.io options, force the following settings.
        options['force new connection'] = true;
        options.reconnect = false;

        // Query params to each socket.io request
        options.query = this.buildSocketIoUrlParams();
        this.logger.info(`connecting to ${this.allocateResult.socketio}?${options.query}`);

        // msg868 - clear all events and disconnect
        this.stopSocketIo();

        this.socket = (this.options.io || io).connect(this.allocateResult.socketio, options);
        if (this.socket === undefined) {
            throw new Error('Can\'t create Socket.io object');
        }

        this.socket.on('connecting', (transportTypeName: string) => { this.onSocketConnecting(transportTypeName); });
        this.socket.on('connect', () => { this.onSocketConnect(); });
        this.socket.on('connect_failed', (reason: string) => { this.onSocketConnectFailed(reason); });
        this.socket.on('disconnect', (reason: string) => { this.onSocketDisconnect(reason); });
        this.socket.on('reconnect', () => { this.onSocketReconnect(); });
        this.socket.on('reconnect_failed', (reason: any) => { this.onSocketReconnectFailed(reason); });
        this.socket.on('reconnecting', () => { this.onSocketReconnecting(); });
        this.socket.on('error', (error: any) => { this.onSocketError(error); });
        this.socket.on('message', (request: string) => { this.onSocketMessage(request); });
        this.socket.on('trouter.connected',
            (eventArgs: ITrouterConnectedEventArgs) => { this.onTrouterConnected(eventArgs); });
        this.socket.on('trouter.reconnect', () => { this.onTrouterReconnect(); });
        this.socket.on('trouter.message_loss',
            (eventArgs: ITrouterMessageLossEventArgs) => { this.onTrouterMessageLoss(eventArgs); });
    }

    public stopSocketIo() {
        if (this.socket) {
            this.logger.info('clearing socket.io');
            try {
                for (const event of [
                    'connecting', 'connect', 'connect_failed', 'disconnect', 'reconnect', 'reconnect_failed',
                    'reconnecting', 'error', 'message', 'trouter.connected', 'trouter.reconnect', 'trouter.message_loss'
                ]) {
                    this.socket.removeAllListeners(event);
                }

                this.socket.disconnect();
                this.logger.debug('cleared socket');
                this.socket = undefined;
            } catch (error) {
                this.logger.error(`exception in disconnecting previous socket. Error: ${toJson(error.stack)}`);
            }
        }
    }

    public dispatchConnected() {
        this.logger.info('dispatching connected');
        this.manager.onConnected(this);
    }

    public dispatchRegistered() {
        this.logger.info('dispatching registered');
        this.manager.onRegistered(this);
    }

    public dispatchUnregistered() {
        this.logger.info('dispatching unregistered');
        this.manager.onUnregistered(this);
    }

    public dispatchDownstreamRequest(request: ITrouterRequest) {
        this.logger.info('dispatching downstream request');

        try {
            const response = new TrouterResponse(<TrouterRequest>request, new ResponseData(request.id),
                (req: TrouterRequest, responseData: ResponseData) => {
                    this.logger.info('sending response to downstream');

                    return this.sendResponse(req, responseData);
                });
            this.manager.onDownstreamRequest(this, request, response);
        } catch (error) {
            this.logger.error(`exception in socket.on message. Error : ${toJson(error.stack)}`);
        }
    }

    public dispatchReconnecting() {
        this.logger.info('dispatching reconnecting');
        this.manager.onReconnecting(this);
    }

    public dispatchReconnectIsRequired() {
        this.logger.info('dispatching reconnect is required by server');
        this.manager.onReconnectIsRequired(this);
    }

    public dispatchDisconnected() {
        this.logger.info('dispatching disconnected');
        this.manager.onDisconnected(this);
    }

    public dispatchTrouterMessageLost(messageLossIndicators: IMessageLossIndicator[]) {
        this.logger.info('dispatching trouter lost message');
        this.manager.onTrouterMessageLost(messageLossIndicators);
    }

    public sendProcessedDroppedIndicators(messageLossIndicators: IMessageLossIndicator[]) {
        try {
            this.logger.info('emitting processed flow tags to the server');
            const processedMessageLossEvent = new DownstreamEvent('trouter.processed_message_loss');
            processedMessageLossEvent.args = {droppedIndicators: messageLossIndicators};
            this.sendDownstreamEvent(processedMessageLossEvent, () => {
                this.logger.info('emitted processed flow tags to the server');
            });
        } catch (error) {
            const msg = toJson(error.stack);
            this.logger.error(`unable to send processed message loss event. Error: ${msg}`);
            this.connectionTracker.trackError('trouter.processed_message_loss', msg, false);
        }
    }

    public resetAllocationConnectionParams() {
        if (this.allocateResult) {
            this.logger.info('Resetting allocation connection parameters');
            this.allocateResult.connectparams = undefined;
        }
    }

    public sendAllocateRequest(token: string) {
        this.connectionAttempt++;
        this.connectionTracker.trackNewConnection();

        const serviceUrl = this.options.trouterUrl;
        let url = serviceUrl;
        let connectParams = this.allocateResult && this.allocateResult.connectparams;
        if (!connectParams) {
            let ecp = this.externalConnectParams;
            if (ecp && ecp.se && parseInt(ecp.se, 10) <= Date.now() + 3600000) {  // 1 hour
                this.logger.warn(`Dropping expired cached connection parameters: ${new Date(parseInt(ecp.se, 10))}`);
                this.externalConnectParams = ecp = undefined;
            }
            if (ecp && ecp.serviceUrl !== url) {
                this.logger.warn(`Dropping cached connection parameters for a different environment (${ecp.serviceUrl}, now ${url})`);
                this.externalConnectParams = ecp = undefined;
            }
            if (ecp && ecp.reconnectUrl) {
                url = ecp.reconnectUrl;
                connectParams = { ...ecp, reconnectUrl: undefined };  // Do not append reconnectUrl as a query parameter
            } else {
                connectParams = ecp;
            }
        }
        url = this.appendCorrelationIds(url, false);
        url = this.appendEndpointId(url, false);
        if (connectParams) {
            url += `&${this.buildQuery(connectParams)}`;
            // Currently server doesn't return "v" in allocate result, but it
            // can be changed, so implement it this way just in case
            if (!connectParams.v) {
                url += `&v=${VERSION}`;
            }
        }

        const allocateRequest = new Request(url, {
            method: 'POST',
            mode: 'cors',
            headers: new Headers({
                'X-Skypetoken': token,
                'Content-Type': 'text/plain'
            })
        });

        this.logger.info(`sendAllocateRequest: POST ${url}`);
        this.connectionTracker.trackStart('allocation');
        let responseStatusCode = -1;
        this.fetchWithTimeout(allocateRequest, this.timeoutOptions.fetchTimeoutMs).then((response: Response) => {
            responseStatusCode = response.status;
            if (!response.ok) {
                throw new Error(response.statusText);
            }

            // tslint:disable-next-line:no-backbone-get-set-outside-model
            const contentType = response.headers.get('content-type');
            if (!contentType || (contentType !== 'application/json' && contentType.substring(0, 17) !== 'application/json;')) {
                throw new Error(`Content-type '${contentType}' is unexpected`);
            }
            this.connectionTracker.trackEnd('allocation');

            return response.json();
        }).then((responseObj) => {
            this.onAllocationResponse(responseObj, serviceUrl);
        }).catch((error: Error) => {
            const msg = `${error}, status code ${responseStatusCode}`;
            this.logger.error(`Allocation request failed. Error: ${msg}`);
            this.connectionTracker.trackError('allocation', msg);
            if (this.externalConnectParams && this.connectionAttempt > 2) {
                if (responseStatusCode >= 400 && responseStatusCode <= 599) {
                    this.logger.warn(`${this.connectionAttempt} connection attempts, server-side failure: erasing external connection parameters`);
                    this.externalConnectParams = undefined;
                } else if (this.externalConnectParams.reconnectUrl) {
                    this.logger.warn(`${this.connectionAttempt} connection attempts, erasing reconnect URL`);
                    delete this.externalConnectParams.reconnectUrl;
                }
            }
            this.fsm.onAllocationFailed(responseStatusCode === 401);
        });
    }

    public sendPingRequest() {
        if (this.socket && this.pingResponseTimerId === undefined) {
            try {
                this.logger.info('emitting ping event');
                this.socket.emit('ping', () => { this.onPingResponse(); });
                this.pingResponseTimerId = setTimeout(() => {
                    this.logger.error('Ping response timeout is fired');
                    this.clearPingResponseTimer();
                    this.fsm.onPingResponseTimeout();
                }, this.timeoutOptions.pongTimeoutMs);
            } catch (error) {
                const msg = toJson(error.stack);
                this.logger.error(`unable to send ping. Error: ${msg}`);
                this.connectionTracker.trackError('ping', msg, false);
            }
        }
    }

    public setUserActivityState(userActivityObject: UserActivityObject) {
        const changed: boolean = (userActivityObject.state !== this.userActivityState.state);
        this.userActivityState = userActivityObject;
        this.fsm.onSetUserActivityState(userActivityObject, changed);
    }

    public sendUserActivityState(reason: UserActivityEventReason, connected: boolean) {
        if (this.userActivityState.state === UserActivityState.Unknown) {
            return;
        }
        if (this.transportTypeName === 'websocket' && connected) {
            if (reason === UserActivityEventReason.Connected) {
                this.sendUserActivityStateMultiple(2);
            } else {
                this.sendUserActivityStateMultiple(1);
            }
        } else if (this.transportTypeName === 'xhr-polling') { // ignoring the connected flag
            if (reason === UserActivityEventReason.Modified) {
                this.fsm.forceReconnect();
            }
        }
    }

    public sendRegisterRequest() {
        if (!this.options.registration || !this.registrarClient) {
            throw new Error('Internal error - options.registration is undefined');
        }
        if (!this.allocateResult) {
            throw new Error('Allocate result is undefined in sendRegisterRequest()');
        }

        this.logger.info('sending register request');
        const span = new Timespan();
        this.connectionTracker.trackStart('registration');

        this.registrarClient.register({
                appId: this.options.registration.pnhAppId,
                aesKey: '',
                languageId: 'en-US',
                platform: this.options.registration.platform,
                templateKey: this.options.registration.pnhTemplateKey,
                platformUIVersion: this.options.registration.platformUIVersion,
                productContext: this.options.registration.productContext
            }, {
                TROUTER: [{
                    context: this.options.registration.context,
                    path: this.allocateResult.surl,
                    ttl: this.getRegistrationTtl()
                }]
            }).then(() => {
                this.logger.info('Register request successful');
                this.connectionTracker.trackEnd('registration');
                this.fsm.onRegistrationSucceed();
                this.connectionTracker.sendTelemetry(ClientEventName.Registration, {
                    duration: span.duration
                }, []);
            }).catch((error: Error) => {
                this.logger.error(`Register request failed. Error: ${error}`);
                this.connectionTracker.trackError('registration', error.message);
                this.fsm.onRegistrationFailed(false);  // Auth challenges are handled within tsregistrar
                this.connectionTracker.sendTelemetry(ClientEventName.Registration, {
                    duration: span.duration
                }, []);
            });
    }

    public sendUnregisterRequest() {
        this.logger.info('sending unregister request');
        const span = new Timespan();
        if (!this.options.registration || !this.registrarClient) {
            throw new Error('Internal error - options.registration is undefined');
        }

        this.connectionTracker.trackStart('unregistration');
        this.registrarClient.unregister().then(() => {
            this.logger.info('Unregister request successful');
            this.connectionTracker.trackEnd('unregistration');
            this.fsm.onUnregistrationSucceed();
            this.connectionTracker.sendTelemetry(ClientEventName.Unregistration, {
                duration: span.duration
            }, []);
        }).catch((error: Error) => {
            this.logger.error(`Unregister request failed. Error: ${error}`);
            this.connectionTracker.trackError('unregistration', error.message);
            this.fsm.onUnregistrationFailed(false);

            this.connectionTracker.sendTelemetry(ClientEventName.Unregistration, {
                duration: span.duration
            }, []);
        });
    }

    /** Wrapper to send request with specified timeout */
    public fetchWithTimeout(request: Request, timeoutMs: number): Promise<Response> {
        return new Promise((resolve, reject) => {
            fetch(request).then(resolve).catch(reject);

            if (timeoutMs !== 0) {
                // tslint:disable-next-line:no-string-based-set-timeout - false positive
                setTimeout(reject, timeoutMs, new Error(`Fetch for '${request.url}' timed out`));
            }
        });
    }

    public resetTokenBackoff() {
        this.tokenBackoff.reset();
    }

    public resetRegisterBackoff() {
        if (this.registrarClient) {
            this.registrarClient.cancelPendingRequests();
        }
    }

    public clearTimers() {
        this.logger.debug('Clearing all pending downstream events related timers');
        for (const key of Object.keys(this.pendingTimers)) {
            this.clearTimer(Number(key));
        }
    }

    public restartIncallModeTimer() {
        this.clearIncallModeTimerId();
        this.logger.debug('Restarting incall mode timer');
        this.incallModeTimerId = setTimeout(() => {
            this.logger.info('Call mode timer fired');
            this.fsm.onIncallModeTimer();
        }, this.options.incallModeTimeoutMs);
    }

    public enterIncallMode() {
        this.logger.info('Entering incall mode');
        this.timeoutOptions = this.options.incallTimeoutOptions;
        this.healthBackoff.setMaxBackoffMs(this.timeoutOptions.maxBackoffMs);
        this.tokenBackoff.setMaxBackoffMs(this.timeoutOptions.maxBackoffMs);
        // Uncomment to enable change of timeout in XHR mode - do it once server start to support it
        // if (this.socket) {
        //     this.socket.socket.options.query = this.buildSocketIoUrlParams();
        // }
        this.inIncallMode = true;
    }

    public exitIncallMode() {
        this.logger.info('Exiting incall mode');
        this.clearIncallModeTimerId();
        this.timeoutOptions = this.options.timeoutOptions;
        this.healthBackoff.setMaxBackoffMs(this.timeoutOptions.maxBackoffMs);
        this.tokenBackoff.setMaxBackoffMs(this.timeoutOptions.maxBackoffMs);
        // Uncomment to enable change of timeout in XHR mode - do it once server start to support it
        // if (this.socket) {
        //     this.socket.socket.options.query = this.buildSocketIoUrlParams();
        // }
        this.inIncallMode = false;
    }

    public isIncallMode() {
        return this.inIncallMode;
    }

    ////////// SocketIO callbacks //////////
    private onSocketConnecting(transportTypeName: string) {
        this.logger.info(`onSocketConnecting(${transportTypeName})`);
        this.transportTypeName = transportTypeName;
        this.connectionTracker.trackProgress('connecting', this.transportTypeName);
        this.fsm.onConnecting();
    }
    private onSocketConnect() {
        this.logger.info('onSocketConnect');
    }
    private onSocketConnectFailed(reason: string): void {
        this.logger.error('onSocketConnectFailed');
        this.connectionTracker.trackError(
            'connect_failed', reason, true, this.transportTypeName ? this.transportTypeName : this.UNKNOWN_TRANSPORT);
        this.fsm.onConnectingFailed();
    }
    private onSocketDisconnect(reason: string): void {
        this.logger.error(`onSocketDisconnect, reason: ${reason}`);
        const props = <Properties>{ reason: reason, serverClosed: !this.fsm.isActive() };
        this.connectionTracker.trackDisconnected(props);
        this.connectionTracker.clearConnectedInfo();
        const sessionLengthMs = props.sessionLengthMS || 0;

        if (reason === 'dup' && this.lastDisconnectReason === 'dup' &&
            sessionLengthMs < this.options.duplicateDisconnectThresholdMs) {
          this.logger.warn(
            'Socket was closed by server as Duplicate for the second time in a row ' +
            `after ${sessionLengthMs} ms which is below the threshold of ` +
            `${this.options.duplicateDisconnectThresholdMs} ms. Resetting cached ` +
            'connection parameters and making a new allocation.');
          this.resetAllocationConnectionParams();
        }
        this.lastDisconnectReason = reason;

        this.fsm.onSocketError(undefined);
        this.connectionExpireTimestampInSecs = undefined;
    }
    private onSocketReconnect(): void {
        // this callback should not be called, because reconnect on socket.io is disabled, but
        // leave it here in case error in socket.io
        this.logger.error('onSocketReconnect');
        this.fsm.onTrouterConnected();
    }
    private onSocketReconnectFailed(reason: any): void {
        // this callback should not be called, because reconnect on socket.io is disabled, but
        // leave it here in case error in socket.io
        this.logger.error(`onSocketReconnectFailed with '${reason}'`);
        this.fsm.onSocketError(typeof reason === 'string' ? reason : undefined);
    }
    private onSocketReconnecting(): void {
        // this callback should not be called, because reconnect on socket.io is disabled, but
        // leave it here in case error in socket.io
        this.logger.error('onSocketReconnecting');
    }
    private onSocketError(errorObject: any): void {
        this.logger.error(`onSocketError with '${toJson(errorObject)}'`);
        this.connectionTracker.trackError('connectSocket', errorObject);
        this.fsm.onSocketError(typeof errorObject === 'string' ? errorObject : undefined);
    }
    private onSocketMessage(requestJson: string): void {
        this.logger.info('onSocketMessage');

        let request: TrouterRequest;
        try {
            request = new TrouterRequest(requestJson);
            const chainId = request.headers && request.headers['X-Microsoft-Skype-Chain-ID'];
            const chainIdLogText = chainId ? ` Chain-Id ${chainId}` : '';
            this.logger.info(`Received request N ${request.id}${chainIdLogText} CV ${request.correlationVector} to '${request.url}'`);
            request.startTS = Date.now();

            if (request.url && this.urlPath && request.url.indexOf(this.urlPath) === 0) {
                request.shortUrl = request.url.substring(this.urlPath.length);
            }
        } catch (error) {
            const msg = toJson(error.stack);
            this.logger.error(`unable to parse request. Error: ${msg}`);
            this.connectionTracker.trackRequest(undefined, msg);
            this.connectionTracker.sendResponseError(`unable to parse request, error: ${error}`);

            return;
        }

        request.timeoutTimerId = setTimeout(() => {
            if (request.replied) {
                return;
            }
            this.logger.error(`Request ${request.id} timed out`);
            const responseData = new ResponseData(request.id);
            responseData.status = 504;
            responseData.headers = { 'Trouter-Responder': 'ClientLib' };
            this.sendResponse(request, responseData);
            request.timedout = true;
        }, this.timeoutOptions.requestTimeoutMs);

        try {
            this.connectionTracker.trackRequest(request);
            this.fsm.onDownstreamRequest(request);
        } catch (error) {
            this.logger.error(`exception in socket.on message. Error: ${toJson(error.stack)}`);
            this.connectionTracker.sendResponseError(error.message, request, undefined);
        }
    }
    private onTrouterConnected(eventArgs: ITrouterConnectedEventArgs): void {
        if (!this.allocateResult) {
            this.logger.error('Invalid internal state - received onTrouterConnected while allocateResult is not set');

            return;
        }
        this.logger.info(`onTrouterConnected: ${this.allocateResult.url}`);

        // Prevent next 'trouter.connected' responses from other trouter nodes
        // hackish way, since socket.io doesn't have API for that
        if (this.socket && this.socket.socket && this.socket.socket.options && this.socket.socket.options.query) {
            this.socket.socket.options.query += '&connected=true';
        }

        // Strip off the first part of the URL, up to the path
        this.urlPath = this.allocateResult.url.replace(/https?:\/\/([A-z0-9\:\$\-\_\.\+\!\*\"\(\)\,]*)\//, '/');

        const isNewUrl = (this.connectedUrl !== this.allocateResult.url);
        this.connectedUrl = this.allocateResult.url;
        this.connectionExpireTimestampInSecs = calculateExpireTsInSec(eventArgs.ttl);

        this.connectionTracker.trackEnd('connectSocket');
        this.connectionTracker.trackConnected(isNewUrl, this.transportTypeName ? this.transportTypeName : this.UNKNOWN_TRANSPORT);

        this.fsm.onTrouterConnected();
    }
    private onTrouterReconnect(): void {
        this.logger.info('onTrouterReconnect');
        this.fsm.onReconnectRequired();
    }
    private onTrouterMessageLoss(eventArgs: ITrouterMessageLossEventArgs): void {
        this.logger.info('onTrouterMessageLoss');

        // Send only array of tags for further processing
        this.fsm.onTrouterMessageLost(eventArgs.droppedIndicators);
    }

    ////////// Other functions //////////
    private onAllocationResponse(allocateResult: IAllocateResult, serviceUrl: string) {
        this.logger.info(`Received allocation response ${JSON.stringify(allocateResult)}`);
        this.allocateResult = allocateResult;
        this.externalConnectParams = undefined;
        // in V4 ttl is a string in IAllocateResult - converting it to number with "+"
        const ttl: number = +allocateResult.ttl;
        // Don't use allocateResult.connectparams.se here since system clock can be
        // incorrect on client's machine, so using TTL is safer
        this.connectionExpireTimestampInSecs = calculateExpireTsInSec(ttl);
        this.connectionId = this.allocateResult.id || '';

        this.connectedClientId = this.allocateResult.ccid;
        this.logger.info(`connected client id set {connectedClientId:${this.connectedClientId}}`);

        this.c2cUrlBase = allocateResult.curlb || '';
        if (this.c2cUrlBase === '') {
            // Try to just remove the port as a fallback if the service didn't provide anything
            let pos = allocateResult.surl.indexOf('://');
            if (pos >= 0) {
                pos = allocateResult.surl.indexOf('/', pos + 3);
                if (pos >= 5 && allocateResult.surl.substr(pos - 5, 5) === ':3443') {
                    this.c2cUrlBase = allocateResult.surl.substr(0, pos - 5);
                }
            }
        }

        const connectionParameters = {
            serviceUrl: serviceUrl,

            // The service does not send reconnectUrl (yet), make it up based
            // on other fields - 'socketio' is simply the root
            reconnectUrl: `${allocateResult.socketio}v4/a`,

            // The spread operator is in this order to allow potentially
            // overwriting 'reconnectUrl' from the service
            ...allocateResult.connectparams
        };

        this.manager.onConnectionParametersUpdated(connectionParameters);
        this.fsm.onAllocationSucceed();
    }

    private onPingResponse() {
        this.logger.info('onPingResponse');
        this.connectionTracker.increasePingResponseCount();
        this.clearPingResponseTimer();
        this.fsm.onPingResponse();
    }

    private clearPingResponseTimer() {
        if (this.pingResponseTimerId !== undefined) {
            clearTimeout(this.pingResponseTimerId);
            this.pingResponseTimerId = undefined;
        }
    }

    private buildQuery(map: any): string {
        const components: any[] = [];
        for (const i of Object.keys(map)) {
            if (map.hasOwnProperty(i) && map[i] !== undefined) {
                components.push(`${i}=${map[i]}`);
            }
        }

        return components.join('&');
    }

    private appendConnectedClientIds(url: string, isParamsOnly: boolean) {
        let params = '';
        if (url.indexOf('ccid=') < 0) {
            params = `ccid=${this.connectedClientId}&`;
        }
        if (this.domId) {
            params += `dom=${this.domId}&`;
        }
        if (params.length > 0) {
            params = params.slice(0, -1);
        }
        const sep = (isParamsOnly || url.indexOf('?') !== -1) ? ('&') : ('?');

        return this.appendCorrelationIds(url + sep + params, isParamsOnly);
    }

    private appendEndpointId(url: string, isParamsOnly: boolean): string {
        const sep = (isParamsOnly || url.indexOf('?') !== -1) ? ('&') : ('?');

        return (url.indexOf('epid') < 0 && this.options.endpointId)
            ? `${url}${sep}epid=${this.options.endpointId}`
            : url;
    }

    private appendCorrelationIds(url: string, isParamsOnly: boolean): string {
        const sep = (isParamsOnly || url.indexOf('?') !== -1) ? ('&') : ('?');

        return (url.indexOf('cor_id') < 0)
            ? `${url}${sep}cor_id=${this.options.clientCorrelationID}`
            + `&con_num=${this.clientID}_${this.connectionAttempt}`
            : url;
    }

    private getHealthUrl(allocateUrl: string): string {
        return `${allocateUrl.substring(0, allocateUrl.lastIndexOf('/a'))}/h`;
    }

    private safeString(str: any): string {
        return (typeof (str) === 'string') ? str : '';
    }

    private sendResponse(request: TrouterRequest, responseData: ResponseData): SendResponseResult {
        if (request.timedout) {
            this.logger.error(`Request ${request.id} already timed out`);

            return SendResponseResult.Timeout;
        }
        if (request.replied) {
            this.logger.error(`Response for request ${request.id} already sent`);

            return SendResponseResult.Duplicate;
        }
        clearTimeout(request.timeoutTimerId);
        request.timeoutTimerId = 0;
        request.replied = true;

        responseData.headers = responseData.headers || {};

        const cv = request.correlationVector;
        this.logger.info(`Sending response for request N ${request.id} CV ${cv} with status ${responseData.status}`);
        if (cv) {
            responseData.headers[cvHeader] = cv;
        }

        // Echo back the incoming "trouter-request" header
        if (request.headers && request.headers['trouter-request'] && !responseData.headers['trouter-request']) {
            responseData.headers['trouter-request'] = request.headers['trouter-request'];
        }
        // Fill in "trouter-client" header
        const latencyMS = Date.now() - request.startTS;
        responseData.headers['trouter-client'] = toJson({
            cd: latencyMS
        });

        this.logger.debug(`response: ${toJson(responseData)}`);

        if (this.socket) {
            try {
                this.socket.send(toJson(responseData));
                responseData.sentTS = Date.now();
                request.incrementCorrelationVector();
                this.connectionTracker.trackResponse(request, latencyMS, responseData);
                if (this.transportTypeName === 'websocket') {
                    // Send ping request immediately to emulate 'ack' from server, so
                    // client can faster react on network issues
                    this.sendPingRequest();
                }

                return SendResponseResult.OK;
            } catch (error) {
                const errorMsg = `unable to send data on response.end. Error: ${toJson(error.stack)}`;
                this.logger.error(errorMsg);
                this.connectionTracker.sendResponseError(errorMsg, request, responseData);

                return SendResponseResult.Disconnected;
            }
        } else {
            this.connectionTracker.sendResponseError('no socket', request, responseData);

            return SendResponseResult.Disconnected;
        }
    }

    /*
        Sends the activity state event to Trouter.
        Has implemented support for sending the current state twice (or multiple times)
        after reconnect, first immediately, and theen the second after configured delay.

        If after the first sent state event the client app changes the state,
        the new state is sent in the second event after the delay.
        Meanwhile the new state is also sent immediately upon
        the change, but the planned second sending (after delay) is triggered anyway,
        to fulfill it's purpose, which is to prevent server-side race condition.
     */
    private sendUserActivityStateMultiple(remaining: number): void {
        const activityEvent = new DownstreamEvent('user.activity');
        const activityEventObject = this.userActivityState.toEventObject();
        activityEvent.args = activityEventObject;
        this.userActivityState.correlationVector.increase();

        this.logger.debug(`Sending user activity '${this.userActivityState.toEventJSON()}', remaining ${remaining - 1}`);
        this.sendDownstreamEvent(activityEvent, () => {
            this.logger.info(`User activity state: ${activityEventObject.state}, cv: ${activityEventObject.cv} accepted`);
            if (this.manager.onUserActivityStateAccepted) {
                this.manager.onUserActivityStateAccepted(activityEventObject.cv);
            }
            this.clearTimer(activityEvent.timeoutTimerId);
            if (remaining > 1) {
                const timerId = setTimeout(() => {
                    this.clearTimer(timerId);
                    this.sendUserActivityStateMultiple(remaining - 1);
                }, this.options.userActivitySecondResendDelayMs);
                this.registerTimer(timerId, 'user.activity/resend');
            }
        });

        activityEvent.timeoutTimerId = setTimeout(() => {
            this.logger.error('Activity state response timeout is fired');
            this.fsm.onActivityStateResponseTimeout();
            this.clearTimer(activityEvent.timeoutTimerId);
        }, this.timeoutOptions.userActivityResponseTimeoutMs);
        this.registerTimer(activityEvent.timeoutTimerId, 'user.activity/response');
    }

    private sendDownstreamEvent(event: DownstreamEvent, responseCallback: () => void) {
        this.logger.info(`Sending downstream event ${event.name}`);

        if (this.socket) {
            this.socket.emit(event.name, event.args, responseCallback);
        }
    }

    private registerTimer(timerId: number, timerDescription: string): void {
        this.logger.debug(`registering timer ${timerId} -> ${timerDescription}`);
        this.pendingTimers[timerId] = timerDescription;
    }

    private clearTimer(id: number): void {
        const timerDescription: string = this.pendingTimers[id];
        this.logger.debug(`clearing timer ${id} -> ${timerDescription}`);
        delete this.pendingTimers[id];
        clearTimeout(id);
    }

    /**
     * Calculate registration interval. Result is based on configuration parameters
     * and allocation ttl value from trouter server
     */
    private getRegistrationTtl(): number {
        const allocationTtl = calculateTtlInSec(this.connectionExpireTimestampInSecs);
        this.logger.debug(`Current connectionID will expire in ${allocationTtl} seconds`);

        if (this.options.registration && this.options.registration.registrarTtlSec && allocationTtl > 0) {
            return Math.min(this.options.registration.registrarTtlSec, allocationTtl);
        } else if (this.options.registration && this.options.registration.registrarTtlSec) {
            return this.options.registration.registrarTtlSec;
        } else if (allocationTtl > 0) {
            return allocationTtl;
        }

        return DEFAULT_REGISTRATION_TIMEOUT_SEC;
    }

    private clearIncallModeTimerId() {
        if (this.incallModeTimerId !== undefined) {
            this.logger.debug('Clearing in-call mode timer');
            clearTimeout(this.incallModeTimerId);
            this.incallModeTimerId = undefined;
        }
    }

    private applyConnectionTrackerOptions(options: ITrouterOptions) {
        try {
            const eventLoggerSent = (options.eventLogger && typeof options.eventLogger.logEvent === 'function');
            if (!eventLoggerSent) {
                this.logger.warn('Trouter client event logging disabled due to invalid configuration.');
            } else {
                this.connectionTracker.mergeSettings(options.telemetrySettings);
                this.connectionTracker.enable(options.eventLogger);
            }
        } catch (error) {
            this.logger.warn(`Trouter client event logging disabled. Error: ${toJson(error.stack)}`);
            this.connectionTracker.disable();
        }
    }
}



// WEBPACK FOOTER //
// ./lib_temp/TrouterConnection.ts